import { z } from 'zod';
import type { HttpService } from '@web/data/services';

/**
 * Shapes mirror the response schemas in `apps/services/accounts/src/api/
 * admin_agent_providers.ts` and `admin_api_keys.ts`. The accounts-api domain
 * package is server-only (Mongo-coupled), so contracts are intentionally
 * redefined locally and parsed with Zod — `ValidationError` will surface
 * any drift between client and server.
 *
 * `Date` fields on the server (`z.date()`) are serialized as ISO strings
 * over the wire, so we type them as `z.string()` here.
 */
const AdminProviderSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  agentExternalRefId: z.string(),
  createdAt: z.string(),
});
export type AdminProviderDto = z.infer<typeof AdminProviderSchema>;

const AdminApiKeySchema = z.object({
  id: z.string(),
  providerId: z.string(),
  prefix: z.string(),
  name: z.string(),
  createdAt: z.string(),
  revokedAt: z.string().nullish(),
  lastUsedAt: z.string().nullish(),
});
export type AdminApiKeyDto = z.infer<typeof AdminApiKeySchema>;

const GeneratedApiKeySchema = AdminApiKeySchema.extend({
  plaintext: z.string(),
  agentExternalRefId: z.string(),
});
type GeneratedApiKey = z.infer<typeof GeneratedApiKeySchema>;

const ProvidersListResponseSchema = z.object({ providers: z.array(AdminProviderSchema) });
const KeysListResponseSchema = z.object({ keys: z.array(AdminApiKeySchema) });

const DeleteProviderResponseSchema = z.object({
  providerId: z.string(),
  revokedKeyCount: z.number().int().nonnegative(),
});
type DeleteProviderResponse = z.infer<typeof DeleteProviderResponseSchema>;

interface AdminApiKeysRepository {
  listProviders: (orgId: string) => Promise<AdminProviderDto[]>;
  createProvider: (orgId: string, name: string) => Promise<AdminProviderDto>;
  listKeys: (orgId: string, providerId: string) => Promise<AdminApiKeyDto[]>;
  generateKey: (orgId: string, providerId: string, name: string) => Promise<GeneratedApiKey>;
  revokeKey: (orgId: string, providerId: string, keyId: string) => Promise<void>;
  deleteProvider: (orgId: string, providerId: string) => Promise<DeleteProviderResponse>;
}

/**
 * Wraps the staff-only `/admin/orgs/{orgId}/agent-providers[/...]` endpoints
 * on accounts-api. Authenticated with the same Cognito user JWT as `me` and
 * `orgs` (the server gates these routes with `createUserAuth` +
 * `createRequireStaff()`), so we route through `accountsHttpService`.
 */
export function createAdminApiKeysRepository(accountsHttp: HttpService): AdminApiKeysRepository {
  async function listProviders(orgId: string): Promise<AdminProviderDto[]> {
    const { providers } = await accountsHttp.get(`/admin/orgs/${orgId}/agent-providers`, {
      schema: ProvidersListResponseSchema,
    });
    return providers;
  }

  async function createProvider(orgId: string, name: string): Promise<AdminProviderDto> {
    return accountsHttp.post(
      `/admin/orgs/${orgId}/agent-providers`,
      { name },
      { schema: AdminProviderSchema },
    );
  }

  async function listKeys(orgId: string, providerId: string): Promise<AdminApiKeyDto[]> {
    const { keys } = await accountsHttp.get(
      `/admin/orgs/${orgId}/agent-providers/${providerId}/api-keys`,
      { schema: KeysListResponseSchema },
    );
    return keys;
  }

  async function generateKey(
    orgId: string,
    providerId: string,
    name: string,
  ): Promise<GeneratedApiKey> {
    return accountsHttp.post(
      `/admin/orgs/${orgId}/agent-providers/${providerId}/api-keys`,
      { name },
      { schema: GeneratedApiKeySchema },
    );
  }

  async function revokeKey(orgId: string, providerId: string, keyId: string): Promise<void> {
    // Server returns 204 No Content — no schema, parsed body is null.
    await accountsHttp.delete(
      `/admin/orgs/${orgId}/agent-providers/${providerId}/api-keys/${keyId}`,
    );
  }

  async function deleteProvider(
    orgId: string,
    providerId: string,
  ): Promise<DeleteProviderResponse> {
    return accountsHttp.delete(`/admin/orgs/${orgId}/agent-providers/${providerId}`, {
      schema: DeleteProviderResponseSchema,
    });
  }

  return { listProviders, createProvider, listKeys, generateKey, revokeKey, deleteProvider };
}
